import { ListingAvailableOptions } from '@nocowanie/noc-api';

import { FilterCheckboxGroupProps } from '@app/components/molecules/filter-checkbox-group';
import { FilterRadioGroupProps } from '@app/components/molecules/filter-radio-group';
import { FilterRangeSliderProps } from '@app/components/molecules/filter-range-slider';
import { FILTER_KEYS } from '@app/consts';
import { SearchFilters } from '@app/models';

export enum FilterComponentType {
    CHECKBOX_GROUP = 'checkbox-group',
    RANGE_SLIDER = 'range-slider',
    RADIO_GROUP = 'radio-group',
}

export const CheckboxFilterGroup = {
    RESERVATION_FEATURES: FILTER_KEYS.RESERVATION_FEATURES,
    PROPERTY_TYPE: FILTER_KEYS.PROPERTY_TYPE,
    PROVISION: FILTER_KEYS.PROVISION,
    AMENITIES: FILTER_KEYS.AMENITIES,
    ROOM_EQUIPMENT: FILTER_KEYS.ROOM_EQUIPMENT,
    RATING: FILTER_KEYS.RATING,
} as const;

export interface CheckboxFilterGroup extends FilterCheckboxGroupProps {
    componentType: FilterComponentType.CHECKBOX_GROUP;
    checkboxGroupType: (typeof CheckboxFilterGroup)[keyof typeof CheckboxFilterGroup];
}

export interface RangeSliderFilterGroup extends FilterRangeSliderProps {
    componentType: FilterComponentType.RANGE_SLIDER;
}

export interface RadioFilterGroup extends FilterRadioGroupProps {
    componentType: FilterComponentType.RADIO_GROUP;
}

export type FilterGroup = CheckboxFilterGroup | RangeSliderFilterGroup | RadioFilterGroup;

/**
 * A single filter entry as returned in `ListingAvailableOptions.filters`.
 */
export type AvailableFilter = NonNullable<ListingAvailableOptions['filters']>[number];

/**
 * The `key` of an available filter entry (e.g. 'Price', 'Amenity', ...).
 */
export type AvailableFilterKey = AvailableFilter['key'];

export interface CreateFilterItemsProps {
    availableOptions?: ListingAvailableOptions | null;
    localFilters: SearchFilters;
    onFilterChange: <K extends keyof SearchFilters>(filterId: K, values: SearchFilters[K]) => void;
    withCounter?: boolean;
}

/**
 * Context handed to every per-key filter creator. Each creator turns a single
 * available filter entry into a renderable `FilterGroup` (or `null` when there
 * is nothing to render for it).
 */
export interface FilterCreatorContext {
    availableFilter: AvailableFilter;
    filters: SearchFilters;
    onFilterChange: CreateFilterItemsProps['onFilterChange'];
}

export type FilterCreator = (context: FilterCreatorContext) => FilterGroup | null;
